import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { forwardRef, useImperativeHandle } from 'react';
import { Edit, Trash } from '../../../icons';
import { BottomSheetContentLayout } from '../../BottomSheet';
import { Card } from '../../Card';
import { Divider } from '../../Divider';
import { IconButton } from '../../IconButton';
import { Column, Row } from '../../Layout';
import { useSheetStack } from '../../OverlayContext/hooks/useSheetStack';
import { SectionCard } from '../../SectionCard';
import { Typography } from '../../Typography';
import styles from '../EditableTableList.module.scss';
import { buttonBi, isEmptyValue } from '../EditableTableList.utils';
import { SortableCard } from '../SortableCard';
import mobileStyles from './EditableTableListMobile.module.scss';
function EditableTableListMobileInner(param, ref) {
    let { rows, columns, getRowId, onRemoveRow, renderRowFooter, editSheetHeader, renderEditSheetFooter, renderEditSheet, onEditDismiss, belowEditableSlot, totalSummary, totalSummaryTestId, mobileCard, disabledRowTooltip, bi, testIds, dnd } = param;
    const { dragEnabled, rowIds, rowIsStatic, withDndContext, withSortableItems } = dnd;
    const editableRowCount = rows.filter((row, rowIndex)=>!rowIsStatic(row, rowIndex)).length;
    const { pushBottomSheetPage, popBottomSheetPage } = useSheetStack();
    const primaryColumn = columns.find((column)=>column.isPrimary);
    const fieldColumns = columns.filter((column)=>!column.isPrimary).map((column, index)=>({
            column,
            index
        })).sort((a, b)=>{
        var _a_column_summaryOrder, _b_column_summaryOrder;
        return ((_a_column_summaryOrder = a.column.summaryOrder) !== null && _a_column_summaryOrder !== void 0 ? _a_column_summaryOrder : a.index) - ((_b_column_summaryOrder = b.column.summaryOrder) !== null && _b_column_summaryOrder !== void 0 ? _b_column_summaryOrder : b.index);
    }).map((param)=>{
        let { column } = param;
        return column;
    });
    const editableColumns = columns.filter((column)=>!column.isPrimary && column.renderEdit != null);
    const renderPrimaryTitle = (row, rowIndex)=>{
        var _ref;
        var _this;
        return (_this = (_ref = primaryColumn === null || primaryColumn === void 0 ? void 0 : primaryColumn.renderSummary) !== null && _ref !== void 0 ? _ref : primaryColumn === null || primaryColumn === void 0 ? void 0 : primaryColumn.renderCell) === null || _this === void 0 ? void 0 : _this(row, rowIndex);
    };
    const openEditSheet = function openEditSheet(row, rowIndex) {
        let isAutoOpen = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false;
        var _ref;
        if (renderEditSheet != null) {
            let committed = false;
            const close = ()=>{
                committed = true;
                popBottomSheetPage();
            };
            pushBottomSheetPage({
                fullHeight: false,
                content: renderEditSheet(row, rowIndex, {
                    close,
                    isAutoOpen
                }),
                onClose: ()=>{
                    if (!committed) onEditDismiss === null || onEditDismiss === void 0 ? void 0 : onEditDismiss(row, rowIndex, isAutoOpen);
                }
            });
            return;
        }
        pushBottomSheetPage({
            fullHeight: false,
            content: /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
                contentGutter: "m",
                header: _object_spread({
                    gutter: 'm'
                }, (_ref = editSheetHeader === null || editSheetHeader === void 0 ? void 0 : editSheetHeader(row, rowIndex)) !== null && _ref !== void 0 ? _ref : {
                    title: /*#__PURE__*/ _jsx(_Fragment, {
                        children: renderPrimaryTitle(row, rowIndex)
                    })
                }),
                footer: renderEditSheetFooter != null ? {
                    content: renderEditSheetFooter(row, rowIndex, {
                        close: popBottomSheetPage
                    })
                } : undefined,
                children: /*#__PURE__*/ _jsx(Column, {
                    gap: 16,
                    className: styles.editSheetContent,
                    children: editableColumns.map((column)=>{
                        var _column_renderEdit;
                        return /*#__PURE__*/ _jsx("div", {
                            "data-testid": testIds.EditField(rowIndex, column.key),
                            children: (_column_renderEdit = column.renderEdit) === null || _column_renderEdit === void 0 ? void 0 : _column_renderEdit.call(column, row, rowIndex)
                        }, column.key);
                    })
                })
            })
        });
    };
    useImperativeHandle(ref, ()=>({
            openEditRow: (rowId)=>{
                const rowIndex = rows.findIndex((row, index)=>getRowId(row, index) === rowId);
                if (rowIndex !== -1) openEditSheet(rows[rowIndex], rowIndex, true);
            }
        }));
    const renderTitleRow = (row, rowIndex)=>/*#__PURE__*/ _jsxs(Row, {
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    weight: "bold",
                    variant: "paragraph1",
                    className: mobileStyles.cardTitle,
                    testId: testIds.CardTitle(rowIndex),
                    children: renderPrimaryTitle(row, rowIndex)
                }),
                rowIsStatic(row, rowIndex) ? disabledRowTooltip != null && /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    className: mobileStyles.titleActions,
                    children: [
                        /*#__PURE__*/ _jsx(IconButton, _object_spread_props(_object_spread({}, buttonBi(bi === null || bi === void 0 ? void 0 : bi.editButton)), {
                            noPadding: true,
                            size: "small",
                            disabled: true,
                            cursorDefault: true,
                            testId: testIds.EditButton(rowIndex),
                            tooltip: {
                                title: disabledRowTooltip,
                                placement: 'top',
                                enableOnMobile: true
                            },
                            children: /*#__PURE__*/ _jsx(Edit, {
                                size: 20,
                                testId: testIds.EditIcon(rowIndex)
                            })
                        })),
                        onRemoveRow != null && /*#__PURE__*/ _jsx(IconButton, _object_spread_props(_object_spread({}, buttonBi(bi === null || bi === void 0 ? void 0 : bi.removeButton)), {
                            noPadding: true,
                            size: "small",
                            disabled: true,
                            cursorDefault: true,
                            testId: testIds.RemoveButton(rowIndex),
                            tooltip: {
                                title: disabledRowTooltip,
                                placement: 'top',
                                enableOnMobile: true
                            },
                            children: /*#__PURE__*/ _jsx(Trash, {
                                size: 20
                            })
                        }))
                    ]
                }) : /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    className: mobileStyles.titleActions,
                    onClick: (event)=>event.stopPropagation(),
                    onPointerDown: (event)=>event.stopPropagation(),
                    children: [
                        /*#__PURE__*/ _jsx(IconButton, _object_spread_props(_object_spread({}, buttonBi(bi === null || bi === void 0 ? void 0 : bi.editButton)), {
                            noPadding: true,
                            size: "small",
                            testId: testIds.EditButton(rowIndex),
                            onClick: ()=>openEditSheet(row, rowIndex),
                            children: /*#__PURE__*/ _jsx(Edit, {
                                size: 20,
                                testId: testIds.EditIcon(rowIndex)
                            })
                        })),
                        onRemoveRow != null && /*#__PURE__*/ _jsx(IconButton, _object_spread_props(_object_spread({}, buttonBi(bi === null || bi === void 0 ? void 0 : bi.removeButton)), {
                            noPadding: true,
                            size: "small",
                            testId: testIds.RemoveButton(rowIndex),
                            onClick: ()=>onRemoveRow(rowIndex),
                            children: /*#__PURE__*/ _jsx(Trash, {
                                size: 20
                            })
                        }))
                    ]
                })
            ]
        });
    const renderFieldRows = (row, rowIndex)=>fieldColumns.map((column)=>{
            var _column_renderSummary;
            const value = ((_column_renderSummary = column.renderSummary) !== null && _column_renderSummary !== void 0 ? _column_renderSummary : column.renderCell)(row, rowIndex);
            if (isEmptyValue(value)) return null;
            const dimmed = rowIsStatic(row, rowIndex) && !column.summaryEmphasis;
            return /*#__PURE__*/ _jsxs(React.Fragment, {
                children: [
                    column.dividerBefore && /*#__PURE__*/ _jsx("div", {
                        "data-testid": testIds.CardFieldDivider(rowIndex, column.key),
                        children: /*#__PURE__*/ _jsx(Divider, {
                            margin: 8
                        })
                    }),
                    column.stacked ? /*#__PURE__*/ _jsxs(Column, {
                        gap: 4,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                skin: dimmed ? 'disabled' : 'secondary',
                                variant: "paragraph2",
                                testId: testIds.CardFieldLabel(rowIndex, column.key),
                                children: column.header
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                ellipsis: true,
                                skin: dimmed ? 'disabled' : undefined,
                                variant: "paragraph2",
                                testId: testIds.CardField(rowIndex, column.key),
                                children: value
                            })
                        ]
                    }) : /*#__PURE__*/ _jsxs(Row, {
                        justifyContent: "space-between",
                        alignItems: "center",
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                skin: dimmed ? 'disabled' : 'secondary',
                                variant: "paragraph2",
                                testId: testIds.CardFieldLabel(rowIndex, column.key),
                                children: column.header
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                skin: dimmed ? 'disabled' : undefined,
                                variant: "paragraph2",
                                testId: testIds.CardField(rowIndex, column.key),
                                children: value
                            })
                        ]
                    })
                ]
            }, column.key);
        });
    const renderRowFooterContent = (row, rowIndex)=>{
        const content = renderRowFooter === null || renderRowFooter === void 0 ? void 0 : renderRowFooter(row, rowIndex);
        return content == null ? null : /*#__PURE__*/ _jsx("div", {
            "data-testid": testIds.RowFooter(rowIndex),
            children: content
        });
    };
    const renderCardContent = (row, rowIndex)=>{
        const footer = renderRowFooterContent(row, rowIndex);
        return /*#__PURE__*/ _jsxs(Card, {
            // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- non-actionable Card (renders as div, never emits BI); edit/remove tracked by inner IconButtons
            noBi: true,
            testId: testIds.Card(rowIndex),
            skin: "white",
            borderSkin: "default",
            padding: "s",
            className: mobileStyles.outerCard,
            children: [
                /*#__PURE__*/ _jsxs(Card, {
                    // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- non-actionable Card (renders as div, never emits BI); edit/remove tracked by inner IconButtons
                    noBi: true,
                    testId: testIds.InnerCard(rowIndex),
                    skin: "mobileGray",
                    borderRadius: "m",
                    padding: "s",
                    className: mobileStyles.innerCard,
                    children: [
                        renderTitleRow(row, rowIndex),
                        renderFieldRows(row, rowIndex)
                    ]
                }),
                footer
            ]
        });
    };
    const list = /*#__PURE__*/ _jsxs(Column, {
        gap: 12,
        testId: testIds.Root(),
        children: [
            editableRowCount === 0 && belowEditableSlot,
            withSortableItems(rows.map((row, rowIndex)=>/*#__PURE__*/ _jsxs(React.Fragment, {
                    children: [
                        dragEnabled && !rowIsStatic(row, rowIndex) ? /*#__PURE__*/ _jsx(SortableCard, {
                            id: rowIds[rowIndex],
                            children: renderCardContent(row, rowIndex)
                        }) : renderCardContent(row, rowIndex),
                        belowEditableSlot != null && rowIndex === editableRowCount - 1 && belowEditableSlot
                    ]
                }, rowIds[rowIndex]))),
            totalSummary != null && /*#__PURE__*/ _jsxs("div", {
                className: mobileStyles.totalSummary,
                "data-testid": totalSummaryTestId,
                children: [
                    totalSummary.label,
                    totalSummary.value
                ]
            })
        ]
    });
    return withDndContext(mobileCard ? // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- non-actionable layout card (renders as div, never emits BI)
    /*#__PURE__*/ _jsx(SectionCard, {
        noBi: true,
        children: list
    }) : list);
}
export const EditableTableListMobile = /*#__PURE__*/ forwardRef(EditableTableListMobileInner);
